import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  ArrowLeft,
  Printer,
  FileText,
  MapPin,
  Calendar,
  User,
  Package,
  ClipboardList,
  StickyNote,
} from "lucide-react";

type CodMaterial = {
  id: number;
  material_name: string;
  quantity: number | null;
  uom: string;
};

type CodDetail = {
  id: number;
  cod_number: string;
  date_received: string;
  received_from: string;
  destruction_location: string | null;
  sales_order: string | null;
  freight_order: string | null;
  vanguard_work_order: string | null;
  client_tag: string | null;
  linked_bol_id: number | null;
  materials: CodMaterial[];
  authorization: {
    name: string | null;
    title: string | null;
    date: string | null;
  };
  notes: string | null;
  status: string;
  created_at: string;
  updated_at: string;
};

function formatDate(dateString: string | null) {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    draft: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    completed: "bg-forest-100 text-forest-700 dark:bg-forest-900/30 dark:text-forest-400",
    pending: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  };
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold capitalize ${
        styles[status] || "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400"
      }`}
    >
      {status}
    </span>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <span className="text-sm text-foreground mt-0.5">{value || "-"}</span>
    </div>
  );
}

export default function ViewCod() {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [cod, setCod] = useState<CodDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);

    apiFetch(`/api/ops/cods/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Failed to load COD (${res.status})`);
        const data = await res.json();
        setCod(data.cod || data);
      })
      .catch((err: any) => {
        setError(err?.message || "Failed to load COD");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, token]);

  const handlePrintPdf = () => {
    if (!id) return;
    const url = `/api/ops/cods/${id}/pdf?token=${token}`;
    window.open(url, "_blank");
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/app/ops/cods")}
            className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="animate-pulse">
            <div className="h-8 w-64 bg-muted rounded mb-2" />
            <div className="h-4 w-40 bg-muted rounded" />
          </div>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-48 bg-muted rounded-xl" />
          <div className="h-32 bg-muted rounded-xl" />
          <div className="h-64 bg-muted rounded-xl" />
        </div>
      </div>
    );
  }

  if (error || !cod) {
    return (
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/app/ops/cods")}
            className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="heading-display text-3xl text-foreground">COD Not Found</h1>
        </div>
        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-xl p-8 text-center">
          <p className="text-sm text-red-700 dark:text-red-400">
            {error || "The requested Certificate of Destruction could not be found."}
          </p>
          <button
            onClick={() => navigate("/app/ops/cods")}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            <ArrowLeft size={14} />
            Back to CODs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/app/ops/cods")}
            className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="heading-display text-3xl text-foreground">
                {cod.cod_number}
              </h1>
              <StatusBadge status={cod.status} />
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Certificate of Destruction
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:ml-auto">
          <button
            onClick={handlePrintPdf}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-forest-600/20"
          >
            <Printer size={16} />
            Print PDF
          </button>
          <button
            onClick={() => navigate("/app/ops/cods")}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary hover:bg-secondary/80 text-foreground text-sm font-semibold rounded-xl transition-colors"
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      {/* Receipt Information */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4">
        <h2 className="text-sm font-bold text-foreground uppercase tracking-wide flex items-center gap-2">
          <FileText size={16} className="text-forest-600 dark:text-forest-400" />
          Receipt Information
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0 mt-0.5">
              <User size={16} className="text-blue-600 dark:text-blue-400" />
            </div>
            <InfoRow label="Received From" value={cod.received_from} />
          </div>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-forest-100 dark:bg-forest-900/30 flex items-center justify-center shrink-0 mt-0.5">
              <Calendar size={16} className="text-forest-600 dark:text-forest-400" />
            </div>
            <InfoRow label="Date Received" value={formatDate(cod.date_received)} />
          </div>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0 mt-0.5">
              <MapPin size={16} className="text-amber-600 dark:text-amber-400" />
            </div>
            <InfoRow label="Destruction Location" value={cod.destruction_location} />
          </div>
        </div>
      </div>

      {/* Customer References */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4">
        <h2 className="text-sm font-bold text-foreground uppercase tracking-wide flex items-center gap-2">
          <ClipboardList size={16} className="text-forest-600 dark:text-forest-400" />
          Customer References
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <InfoRow label="Sales Order #" value={cod.sales_order} />
          <InfoRow label="Freight Order #" value={cod.freight_order} />
          <InfoRow label="Vanguard Work Order #" value={cod.vanguard_work_order} />
        </div>
        {cod.client_tag && (
          <div className="pt-2 border-t border-border/60">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mr-2">
              Client Tag:
            </span>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
                cod.client_tag === "vanguard"
                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                  : cod.client_tag === "willcox"
                  ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                  : cod.client_tag === "3lag"
                  ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                  : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400"
              }`}
            >
              {cod.client_tag}
            </span>
          </div>
        )}
      </div>

      {/* Materials */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="p-6 border-b border-border">
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wide flex items-center gap-2">
            <Package size={16} className="text-forest-600 dark:text-forest-400" />
            Materials ({cod.materials?.length || 0})
          </h2>
        </div>
        {!cod.materials || cod.materials.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            No materials recorded.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 w-12">#</th>
                  <th className="px-4 py-3">Material</th>
                  <th className="px-4 py-3">Quantity</th>
                  <th className="px-4 py-3">Unit</th>
                </tr>
              </thead>
              <tbody>
                {cod.materials.map((material, index) => (
                  <tr
                    key={material.id}
                    className="border-b border-border/60 last:border-0"
                  >
                    <td className="px-4 py-3 text-muted-foreground tabular-nums">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3 font-medium text-foreground">
                      {material.material_name}
                    </td>
                    <td className="px-4 py-3 text-foreground tabular-nums">
                      {material.quantity != null ? material.quantity.toLocaleString() : "-"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {material.uom || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Authorization */}
      {cod.authorization && (cod.authorization.name || cod.authorization.title || cod.authorization.date) && (
        <div className="bg-card border border-border rounded-xl p-6 space-y-4">
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wide flex items-center gap-2">
            <User size={16} className="text-forest-600 dark:text-forest-400" />
            Authorization
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <InfoRow label="Authorized By" value={cod.authorization.name} />
            <InfoRow label="Title" value={cod.authorization.title} />
            <InfoRow label="Date" value={formatDate(cod.authorization.date)} />
          </div>
        </div>
      )}

      {/* Notes */}
      {cod.notes && (
        <div className="bg-card border border-border rounded-xl p-6 space-y-4">
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wide flex items-center gap-2">
            <StickyNote size={16} className="text-forest-600 dark:text-forest-400" />
            Notes
          </h2>
          <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
            {cod.notes}
          </p>
        </div>
      )}

      {/* Metadata footer */}
      <div className="bg-secondary/50 border border-border rounded-xl p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
        <span>
          Created: {formatDate(cod.created_at)}
        </span>
        <span>
          Last updated: {formatDate(cod.updated_at)}
        </span>
        {cod.linked_bol_id && (
          <span>
            Linked BOL ID: {cod.linked_bol_id}
          </span>
        )}
      </div>
    </div>
  );
}
